import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Reusable modal form.
 * fields: [{ name, label, type: 'text'|'textarea'|'select'|'date', placeholder?, options?, required? }]
 * initialValues: object to pre-fill (edit mode)
 */
export const EntryModal = ({
  open,
  onOpenChange,
  title = "Add Entry",
  description,
  fields = [],
  submitLabel = "Save",
  initialValues,
  onSubmit,
}) => {
  const [values, setValues] = useState({});

  useEffect(() => {
    if (open) setValues(initialValues ? { ...initialValues } : {});
  }, [open, initialValues]);

  const setField = (name, v) => setValues((prev) => ({ ...prev, [name]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = fields.filter((f) => f.required && !values[f.name]);
    if (missing.length) {
      toast.error(`Please complete: ${missing.map((m) => m.label).join(", ")}`);
      return;
    }
    try {
      await onSubmit?.(values);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="entry-modal"
        className="max-h-[90vh] max-w-lg overflow-y-auto border-[#E5E2D9] bg-white"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-medium tracking-tight text-[#0D1B2A]">
            {title}
          </DialogTitle>
          <DialogDescription className="text-[#5C6B7B]">
            {description || "Complete the fields below and save."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-1">
          {fields.map((f) => (
            <div key={f.name} className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#5C6B7B]">
                {f.label}
                {f.required && <span className="ml-0.5 text-[#9B2226]">*</span>}
              </Label>
              {f.type === "textarea" ? (
                <Textarea
                  data-testid={`field-${f.name}`}
                  value={values[f.name] || ""}
                  onChange={(e) => setField(f.name, e.target.value)}
                  placeholder={f.placeholder}
                  rows={3}
                  className="resize-y border-[#E5E2D9] focus-visible:ring-[#C5A059]/40"
                />
              ) : f.type === "select" ? (
                <Select
                  value={values[f.name] || ""}
                  onValueChange={(v) => setField(f.name, v)}
                >
                  <SelectTrigger data-testid={`field-${f.name}`} className="border-[#E5E2D9]">
                    <SelectValue placeholder={f.placeholder || "Select…"} />
                  </SelectTrigger>
                  <SelectContent>
                    {f.options?.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  type={f.type === "date" ? "date" : "text"}
                  data-testid={`field-${f.name}`}
                  value={values[f.name] || ""}
                  onChange={(e) => setField(f.name, e.target.value)}
                  placeholder={f.placeholder}
                  className="border-[#E5E2D9] focus-visible:ring-[#C5A059]/40"
                />
              )}
            </div>
          ))}

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="entry-modal-cancel"
              className="border-[#E5E2D9]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="entry-modal-submit"
              className="bg-[#0D1B2A] text-white hover:bg-[#1B2A3B]"
            >
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
